/**
 * Footer Component
 * SEO-optimized footer with internal linking
 */

import React from 'react';
import { Link } from 'react-router-dom';
import BookAppointmentLink from './BookAppointmentLink';
import { services, siteConfig } from '../data/seo';
import { trackPhoneClick, trackEmailClick } from '../utils/analytics';

const Footer: React.FC = () => {

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="container-custom section-padding">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5 mb-8 lg:mb-12">
          {/* Brand Section */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="mb-4 lg:mb-6">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <img 
                  src="/Chomp_White_Logo.png" 
                  alt="CHOMP Pet Dental — footer logo"
                  width="48"
                  height="48"
                  className="w-12 h-12"
                  loading="lazy"
                />
                <h3 className="text-xl sm:text-2xl font-bold text-brand-teal">
                  CHOMP Pet Dental
                </h3>
              </Link>
              <p className="text-gray-400 italic text-sm sm:text-base mt-2">
                The Future of Pet Dental Care
              </p>
            </div>
            <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 lg:mb-6">
              South Florida's first veterinary clinic 100% dedicated to pet dental care. 
              Dental care for dogs and cats in Fort Lauderdale.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href={`tel:${siteConfig.phone}`}
                onClick={() => trackPhoneClick('footer')}
                className="flex items-center gap-3 text-gray-300 hover:text-brand-teal transition-colors"
              >
                <i className="fas fa-phone text-brand-teal w-5"></i>
                <span>{siteConfig.phoneDisplay}</span>
              </a>
              <a 
                href={`mailto:${siteConfig.email}`}
                onClick={() => trackEmailClick('footer', siteConfig.email)}
                className="flex items-center gap-3 text-gray-300 hover:text-brand-teal transition-colors"
              >
                <i className="fas fa-envelope text-brand-teal w-5"></i>
                <span>{siteConfig.email}</span>
              </a>
              <div className="flex items-start gap-3 text-gray-300">
                <i className="fas fa-map-marker-alt text-brand-teal w-5 mt-1"></i>
                <div>
                  <div>{siteConfig.address.street}</div>
                  <div>{siteConfig.address.city}, {siteConfig.address.state} {siteConfig.address.zip}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Services Links */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Our Services</h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.slug}>
                  <span className="text-sm sm:text-base text-gray-300">
                    {service.name}
                  </span>
                </li>
              ))}
              <li>
                <Link 
                  to="/services" 
                  className="text-sm sm:text-base text-brand-teal hover:underline font-medium"
                >
                  View All Services →
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Location */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Location</h4>
            <ul className="space-y-2">
              <li>
                <span className="text-sm sm:text-base text-gray-300">Fort Lauderdale, FL</span>
              </li>
            </ul>
          </div>

          {/* Resources & Company */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Company</h4>
            <ul className="space-y-2">
              <li><Link to="/blog/pet-dental-cleaning-cost-fort-lauderdale" className="text-sm sm:text-base text-gray-300 hover:text-brand-teal transition-colors">Dental Cleaning Costs</Link></li>
              <li><Link to="/blog" className="text-sm sm:text-base text-gray-300 hover:text-brand-teal transition-colors">Pet Dental Guides</Link></li>
              <li>
                <Link to="/team" className="text-sm sm:text-base text-gray-300 hover:text-brand-teal transition-colors">
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/research" className="text-sm sm:text-base text-gray-300 hover:text-brand-teal transition-colors">
                  Research
                </Link>
              </li>
              <li>
                <Link to="/referrals" className="text-sm sm:text-base text-gray-300 hover:text-brand-teal transition-colors">
                  Vet Referrals
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm sm:text-base text-gray-300 hover:text-brand-teal transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <BookAppointmentLink analyticsSource="footer" className="text-sm sm:text-base text-brand-teal hover:underline font-medium">
                  Book Appointment →
                </BookAppointmentLink>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="text-gray-400 text-xs sm:text-sm text-center sm:text-left">
                © {new Date().getFullYear()} CHOMP Pet Dental. All rights reserved.
              </div>
              <div className="flex items-center gap-4 text-xs sm:text-sm">
                <Link to="/privacy-policy" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-gray-600">|</span>
                <Link to="/terms-of-service" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Terms of Service
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {siteConfig.social.facebook && (
                <a 
                  href={siteConfig.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-brand-teal transition-colors"
                  aria-label="Facebook (opens in new tab)"
                >
                  <i className="fab fa-facebook-f text-lg" aria-hidden="true"></i>
                </a>
              )}
              {siteConfig.social.instagram && (
                <a 
                  href={siteConfig.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-brand-teal transition-colors"
                  aria-label="Instagram (opens in new tab)"
                >
                  <i className="fab fa-instagram text-lg" aria-hidden="true"></i>
                </a>
              )}
              {siteConfig.social.yelp && (
                <a 
                  href={siteConfig.social.yelp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-brand-teal transition-colors"
                  aria-label="Yelp (opens in new tab)"
                >
                  <i className="fab fa-yelp text-lg" aria-hidden="true"></i>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
